import React, { useState } from 'react';
import { Card, CardContent, CardMedia, CardActions, Typography, Box, Chip, Button, IconButton, Stack, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, OpenInNew, CheckCircle, Cancel } from '@mui/icons-material';

const currencyOptions = {
  USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', JPY: '¥', ZAR: 'R'
};

function PropertyCard({ listing, currency, amenitiesList }) {
  const [imgIdx, setImgIdx] = useState(0);
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const images = listing.images && listing.images.length > 0 ? listing.images : ["https://via.placeholder.com/400x250?text=No+Image"];
  const currencySymbol = currencyOptions[currency] || '$';

  // Map amenity IDs to amenity objects
  const amenityMap = Object.fromEntries((amenitiesList || []).map(a => [a.id, a]));
  const availableAmenities = (listing.amenities || []).map(id => amenityMap[id]).filter(Boolean);
  const availableIds = new Set((listing.amenities || []));
  // Group all amenities by category
  const groupedAmenities = (amenitiesList || []).reduce((acc, a) => {
    acc[a.category] = acc[a.category] || [];
    acc[a.category].push(a);
    return acc;
  }, {});

  const handlePrev = e => {
    e.stopPropagation();
    setImgIdx(idx => (idx === 0 ? images.length - 1 : idx - 1));
  };
  const handleNext = e => {
    e.stopPropagation();
    setImgIdx(idx => (idx === images.length - 1 ? 0 : idx + 1));
  };

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      borderRadius: 3,
      maxWidth: { xs: 320, sm: 400, md: 450 },
      width: '100%'
    }}>
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="180"
          image={images[imgIdx]}
          alt={listing.title}
          sx={{ objectFit: 'cover' }}
        />
        {images.length > 1 && (
          <>
            <IconButton 
              onClick={handlePrev} 
              sx={{ 
                position: 'absolute', 
                top: '50%', 
                left: 8, 
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
              }} 
              size="small"
            >
              <ArrowBackIos fontSize="small" />
            </IconButton>
            <IconButton 
              onClick={handleNext} 
              sx={{ 
                position: 'absolute', 
                top: '50%', 
                right: 8, 
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
              }} 
              size="small"
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
            <Box sx={{ 
              position: 'absolute', 
              bottom: 8, 
              right: 16, 
              bgcolor: 'rgba(0,0,0,0.7)', 
              color: 'white', 
              px: 1.5, 
              py: 0.5,
              borderRadius: 2, 
              fontSize: 12 
            }}>
              {imgIdx + 1} / {images.length}
            </Box>
          </>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem' } }}>
          {listing.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {listing.location}
        </Typography>
        <Typography variant="h5" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {currencySymbol}{listing.price}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.4 }}>
          {listing.description}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
          {availableAmenities.slice(0, 3).map((a, i) => (
            <Chip 
              key={i} 
              label={a.name} 
              size="small" 
              variant="outlined"
              sx={{ borderRadius: 1, fontSize: { xs: '0.625rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
            />
          ))}
          {availableAmenities.length > 3 && (
            <Chip 
              label={`+${availableAmenities.length - 3} more`} 
              size="small" 
              variant="outlined"
              sx={{ borderRadius: 1, fontSize: { xs: '0.625rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
            />
          )}
        </Stack>
      </CardContent>
      <CardActions sx={{ p: { xs: 2, sm: 2.5 }, pt: 0 }}>
        <Button
          href={listing.airbnb_link}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
          sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          endIcon={<OpenInNew />}
          fullWidth
        >
          View on Airbnb
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          fullWidth
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          onClick={() => setAmenitiesOpen(true)}
        >
          View Amenities
        </Button>
        <Dialog open={amenitiesOpen} onClose={() => setAmenitiesOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Amenities</DialogTitle>
          <DialogContent>
            {Object.entries(groupedAmenities).map(([category, items]) => (
              <Box key={category} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>{category}</Typography>
                <List dense>
                  {items.map(a => (
                    <ListItem key={a.id}>
                      <ListItemIcon>
                        {category === 'Not included' ? (
                          <Cancel color="error" />
                        ) : availableIds.has(a.id) ? (
                          <CheckCircle color="success" />
                        ) : (
                          <Cancel color="disabled" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={a.name.replace(/^Unavailable: /, '')}
                        sx={{ color: category === 'Not included' ? 'error.main' : availableIds.has(a.id) ? 'inherit' : 'text.disabled' }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Divider />
              </Box>
            ))}
          </DialogContent>
        </Dialog>
      </CardActions>
    </Card>
  );
}

export default PropertyCard;
